import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography, Box, Button, TextField, MenuItem, IconButton,
  Select, Card, Grid, Tooltip, CircularProgress
} from '@mui/material';
import Header from '../../Header/Header';
import Footer2 from '../Footer2';
import Internships from '../../Icon/Internship.png';
import Jobs from '../../Icon/Jobs2.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useAuth } from '../../../AuthContext';
import Header1 from '../../Header/Header1';
import CareersHero from './CarrersHero';
import CareersFeatures from './CareersFeatures';

const API_URL = process.env.REACT_APP_API_URL;

function Careers({ onLogout }) {
  const { userId } = useAuth();
  const [internshipRole, setInternshipRole] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [resume, setResume] = useState(null);
  const [showForm, setShowForm] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);

  // Timer while loading
  useEffect(() => {
    let timer;

    if (loading) {
      setLoadingSeconds(0);
      timer = setInterval(() => {
        setLoadingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timer);
      setLoadingSeconds(0);
    }

    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const validateInputs = () => {
    if (!name || !email || !contact || !resume) {
      alert("All fields are required."); return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("Invalid email."); return false;
    }
    if (!/^\d{10}$/.test(contact)) {
      alert("Contact must be 10 digits."); return false;
    }
    return true;
  };

  const handleApply = async () => {
    if (!validateInputs()) return;

    const type = showForm;          // either "internship" or "job"
    const role = type === 'internship' ? internshipRole : jobRole;

    const data = new FormData();
    data.append("name", name);
    data.append("contact", contact);
    data.append("email", email);
    data.append("role", role);
    data.append("type", type);
    data.append("is_active", true);
    data.append("resume", resume);

    setLoading(true);

    try {
      await axios.post(`${API_URL}/create_application/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert(`Applied for ${type.toUpperCase()}: ${role}`);
      window.location.reload();
    } catch (e) {
      alert("Submission error.");
    }

    setLoading(false);
  };

  const handleFile = e => {
    const file = e.target.files[0];
    if (file?.size > 1 * 1024 * 1024) {
      alert("Max 1 MB file size.");
      return;
    }
    setResume(file);
  };

  const removeFile = () => setResume(null);

  // ---------- FIXED FORM LOGIC ----------
  // showForm can be only "internship" or "job"

  const renderForm = () => (
    <Card sx={{
      mt: 3, p: 3, width: '100%', maxWidth: 500,
      bgcolor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(6px)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
    }}>

      <Typography variant="h6" align="center" mb={2} sx={{ color: '#fff' }}>
        {showForm === 'internship' ? 'Internship Application' : 'Job Application'}
      </Typography>

      <Select
        fullWidth
        value={showForm === 'internship' ? internshipRole : jobRole}
        onChange={e =>
          showForm === 'internship' ? setInternshipRole(e.target.value) : setJobRole(e.target.value)
        }
        displayEmpty
        sx={{
          mb: 2, bgcolor: '#222', color: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1E90FF' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00CFFF' },
        }}
      >
        <MenuItem value="" disabled>Select Role</MenuItem>

        {showForm === 'internship'
          ? ['Software Developer','Data Science', 'Social Media Marketing', 'Content Creation','Artificial Intelligence']
              .map(role => <MenuItem key={role} value={role}>{role}</MenuItem>)
          : ['Lead Generation Specialist', 'Data Science', 'AI Engineer', 'Marketing Specialist', 'B2B Sales Specialist', 'AI Enrollment Specialist']
              .map(role => <MenuItem key={role} value={role}>{role}</MenuItem>)
        }
      </Select>

      {['name', 'contact', 'email'].map((field) => (
        <TextField
          key={field}
          label={field.charAt(0).toUpperCase() + field.slice(1)}
          value={{ name, contact, email }[field]}
          onChange={e =>
            ({ name: setName, contact: setContact, email: setEmail }[field](e.target.value))
          }
          fullWidth
          variant="outlined"
          sx={{
            mb: 2, bgcolor: '#222',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#555' },
              '&:hover fieldset': { borderColor: '#1E90FF' },
              '&.Mui-focused fieldset': { borderColor: '#00CFFF' },
              '& input': { color: '#fff' }
            }
          }}
        />
      ))}

      <Box
        onClick={() => document.getElementById('fileInput').click()}
        sx={{
          border: '2px dashed #777', borderRadius: 2, py: 2,
          textAlign: 'center', bgcolor: resume ? 'rgba(30,144,255,0.1)' : '#111',
          cursor: 'pointer', mb: 2
        }}
      >
        <input id="fileInput" type="file" accept=".pdf,.doc,.docx" hidden onChange={handleFile} />
        <Typography sx={{ color: '#fff' }}>
          {resume
            ? <>
                {resume.name}
                <Button size="small" onClick={removeFile} sx={{ ml: 1, color: 'red' }}>Remove</Button>
              </>
            : 'Click to upload resume (PDF, DOC, max 1MB)'}
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          py: 1.5,
          bgcolor: 'linear-gradient(to right, #1E90FF, #00CFFF)',
          color: '#fff',
          '&:hover': { bgcolor: 'linear-gradient(to right, #00CFFF, #1E90FF)' }
        }}
        onClick={handleApply}
      >
        Submit
      </Button>
    </Card>
  );

  return (
    <Box sx={{ bgcolor: '#000', minHeight: '100vh', pb: 6 }}>
      
      {userId ? (
        <Header1 userId={userId} onLogout={onLogout} />
      ) : (
        <Header />
      )}
      <CareersHero/>
      {/* <CareersFeatures/> */}

      <Box sx={{ textAlign: 'center', pt: 8, px: 2 }}>
        <Typography variant="h2" sx={{ color: '#fff', fontWeight: 700,  }}>
          Explore open roles
        </Typography>


        {/* APPLY BOXES */}
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {[
            { label: 'Internships', key: 'internship', img: Internships },
            { label: 'Jobs', key: 'job', img: Jobs }
          ].map((plan) => (
            <Box key={plan.key} sx={{
              width: '90%', maxWidth: 500, p: 2, my: 3,
              bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
            }}>

              <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                {plan.label}
              </Typography>

              <Box component="img"
                src={plan.img}
                alt={plan.label}
                sx={{ width: '100%', mb: 2, borderRadius: 1 }}
              />

              <Button
                variant="contained"
                sx={{
                  px: 4, py: 1.5,
                  bgcolor: 'linear-gradient(to right, #1E90FF, #00CFFF)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'linear-gradient(to right, #00CFFF, #1E90FF)' }
                }}
                onClick={() =>
                  setShowForm(showForm === plan.key ? null : plan.key)
                }
              >
                Apply
              </Button>

              {showForm === plan.key && renderForm()}
            </Box>
          ))}
        </Box>
      </Box>

      {/* SCROLL BUTTON */}
      <Tooltip title="Scroll to Top">
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed', bottom: 20, right: 20,
            bgcolor: 'rgba(30,144,255,0.9)', color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,206,255,0.9)' }
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </Tooltip>

      <Footer2 />

      {/* LOADING OVERLAY */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress
              size={60}
              thickness={6}
              sx={{
                color: 'transparent',
                '& .MuiCircularProgress-circle': {
                  stroke: 'url(#better-gradient)',
                },
              }}
            />

            <svg width="0" height="0">
              <defs>
                <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f30b96ff" />
                  <stop offset="50%" stopColor="#221ce0ff" />
                  <stop offset="100%" stopColor="#0eacebff" />
                </linearGradient>
              </defs>
            </svg>

            <Typography
              variant="h6"
              sx={{
                mt: 2,
                color: '#fff',
                textShadow: '0 1px 3px rgba(0,0,0,0.7)',
              }}
            >
              Applying... ({loadingSeconds} sec)
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Careers;
